"use client";
import React from "react";
import cx from "classnames";
import { useField } from "providers/FormProvider";

/**
 * Field Tailwind cho form hợp tác, nối Formik qua useField.
 * Không dùng components/common/Formik/Input (AntD) vì landing này đã chốt giao diện
 * ô nhập bo tròn 12px, viền xám nhạt, không theo theme AntD.
 */

const inputCls =
  "w-full rounded-xl border px-4 py-3 text-[16px] md:text-[15px] outline-none transition-colors focus:ring-2 bg-white";
const okCls = "border-gray-200 focus:border-primary focus:ring-primary/20";
const errCls = "border-red-400 focus:border-red-400 focus:ring-red-100";
const labelCls = "block text-sm font-semibold text-black01 mb-1.5";

interface BaseProps {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}

const Label: React.FC<{ htmlFor: string; required?: boolean; children: React.ReactNode }> = ({
  htmlFor,
  required,
  children,
}) => (
  <label className={labelCls} htmlFor={htmlFor}>
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const ErrorText: React.FC<{ id: string; error?: string; touched?: boolean }> = ({
  id,
  error,
  touched,
}) =>
  touched && error ? (
    <p id={id} role="alert" className="mt-1.5 text-[13px] text-red-500 !mb-0">
      {error}
    </p>
  ) : null;

export const TextField: React.FC<
  BaseProps & {
    type?: "text" | "tel" | "email";
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    list?: string;
    autoComplete?: string;
  }
> = ({ name, label, required, className, type = "text", ...rest }) => {
  const [field, meta] = useField<string>(name);
  const invalid = !!(meta.touched && meta.error);
  return (
    <div className={className}>
      <Label htmlFor={name} required={required}>
        {label}
      </Label>
      <input
        id={name}
        type={type}
        className={cx(inputCls, invalid ? errCls : okCls)}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${name}-error` : undefined}
        {...field}
        {...rest}
      />
      <ErrorText id={`${name}-error`} error={meta.error} touched={meta.touched} />
    </div>
  );
};

export const TextareaField: React.FC<BaseProps & { rows?: number }> = ({
  name,
  label,
  required,
  className,
  rows = 3,
  ...rest
}) => {
  const [field, meta] = useField<string>(name);
  const invalid = !!(meta.touched && meta.error);
  return (
    <div className={className}>
      <Label htmlFor={name} required={required}>
        {label}
      </Label>
      <textarea
        id={name}
        rows={rows}
        className={cx(inputCls, "resize-none", invalid ? errCls : okCls)}
        aria-invalid={invalid || undefined}
        {...field}
        {...rest}
      />
      <ErrorText id={`${name}-error`} error={meta.error} touched={meta.touched} />
    </div>
  );
};

export const SelectField: React.FC<
  BaseProps & {
    options: string[] | { value: string; label: string }[];
    emptyLabel?: string;
  }
> = ({ name, label, required, className, options, emptyLabel = "Chọn" }) => {
  const [field, meta] = useField<string>(name);
  const invalid = !!(meta.touched && meta.error);
  // Hỗ trợ cả options cũ (string[], value = label) và options mới
  // ({value,label}[]) khi label cần dịch theo locale (vd CATEGORIES).
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  return (
    <div className={className}>
      <Label htmlFor={name} required={required}>
        {label}
      </Label>
      <select
        id={name}
        className={cx(inputCls, invalid ? errCls : okCls)}
        aria-invalid={invalid || undefined}
        {...field}
      >
        <option value="" disabled>
          {emptyLabel}
        </option>
        {normalized.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ErrorText id={`${name}-error`} error={meta.error} touched={meta.touched} />
    </div>
  );
};
